import './LinearOptimizations.scss';
import { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { MdAllOut, MdAttachMoney, MdBubbleChart, MdNotInterested, MdPeople } from 'react-icons/md';
import { Link, useParams } from 'react-router-dom';
import { AudiencePickerModal } from '@Components/AudiencePickerModal';
import { PRETTY_DEMO_NAMES } from '@Components/AudiencePickerModal/audience-picker-constants';
import { Button } from '@Components/Button';
import { ConstraintSaveBar } from '@Components/ConstraintSaveBar/ConstraintSaveBar';
import { Appearance, Size } from '@Components/designConstants';
import { ModalEditWidget } from '@Components/ModalEditWidget';
import {
  CustomModalEditWidgetHeader,
  SaveChangesInput,
  SelectorOption
} from '@Components/ModalEditWidget/modal-edit-constants';
import { NumberFormatter } from '@Components/NumberFormatter/NumberFormatter';
import { Page } from '@Components/Page/Page';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { CONSTRAINT_WARNING_HEADER } from '@Components/ConstraintWarning/constraint-warning-constants';
import {
  LinearAudienceConstraint as AudienceConstraint,
  LinearAvoidConstraint as AvoidConstraint,
  LinearBudgetConstraint as BudgetConstraint,
  LinearGlobalSettings as GlobalConstraints,
  LinearSpotConstraint as SpotConstraint
} from '@blisspointmedia/bpm-types/dist/OptimizationConstraints';
import { useCreativeMap } from '../../redux/creative';
import { useSetError } from '../../redux/modals';
import {
  applyTableChanges,
  formatOptimizationDate,
  withIndexes
} from '../../utils/optimization-utils';
import { useCurrencyInputDisplay } from '../../utils/hooks/useCurrencyInputDisplay';
import { useConstraintDraft } from '../../utils/hooks/useConstraintDraft';
import { parseWholeUsd } from '../../utils/text-utils';
import {
  useSaveConstraint,
  useConstraintData,
  validateLinearConstraint
} from './use-linear-constraint-data';
import { useCreativeInsights, useNetworkRotations } from './use-linear-network-rotations';
import { LinearOptimizationPageProps, OptimizationType } from './linear-optimizations-constants';
import { demographicKeys } from './linear-optimizations-utils';
import {
  checkIsAudienceValid,
  checkIsAvoidValid,
  checkIsBudgetValid,
  checkIsGlobalValid,
  checkIsSpotValid
} from './constraint-view-validators';

const ROTATION_OPTIONS = [
  'Overnight',
  'Early Morning',
  'Daytime',
  'Early Fringe',
  'Prime',
  'Late Fringe',
  'Broad Rotator',
  'Weekend'
].map(value => ({ label: value, value }));
const AVAIL_OPTIONS: SelectorOption[] = [
  { label: 'National', value: 'N' },
  { label: 'Local', value: 'L' }
];
const LENGTH_OPTIONS: SelectorOption[] = ['15', '30', '60', '30s Equiv'].map(value => ({
  label: value,
  value
}));
const METRIC_OPTIONS: SelectorOption[] = ['TRPs', 'Impressions'].map(value => ({
  label: value,
  value
}));

// [display name, backing field on Constraints.global] -- fixed set, not user-addable/deletable.
const GLOBAL_FIELD_DEFINITIONS: [string, keyof GlobalConstraints][] = [
  ['Single 15s Spot Max Cost', 'single15sSpotMaxCost'],
  ['Single 30s Spot Max Cost', 'single30sSpotMaxCost'],
  ['Single 60s Spot Max Cost', 'single60sSpotMaxCost'],
  ['Max per Network Spend %', 'networkMaxSpendPct'],
  ['Max Hourly per Network/Day/Daypart Spend', 'networkDayDaypartMaxSpendPerHour'],
  [
    'Max Hourly per Satellite Network/Day/Daypart Spend',
    'networkDayDaypartSatelliteMaxSpendPerHour'
  ],
  ['Max Hourly Spots per Network/Day/Daypart', 'networkDayDaypartCountPerHour'],
  [
    'Max Hourly Spots per Network/Day/Daypart/Avail (National)',
    'networkDayDaypartReachTypeCountPerHour'
  ],
  [
    'Max Hourly Spots per Network/Day/Daypart/Avail (Local)',
    'networkDayDaypartReachTypeLocalCountPerHour'
  ],
  ['Max Hourly Spots per Satellite Network/Day/Daypart', 'networkSatelliteDayDaypartCountPerHour'],
  ['Total Spots', 'totalSpots'],
  ['Network/Avail/Rotation Relaxation Start (value - value + 3)', 'minExtrapolation'],
  ['Unseen Max Spend %', 'unseenMaxSpendPct']
];

interface GlobalRow {
  index: number;
  name: string;
  key: keyof GlobalConstraints;
  value: number;
}

const globalValueFormat = (
  key: keyof GlobalConstraints
): 'percent' | 'currency' | 'real' | 'decimal' => {
  if (key.includes('Pct')) return 'percent';
  if (key.includes('Spend') || key.includes('Cost')) return 'currency';
  if (key.includes('CountPer')) return 'real';
  return 'decimal';
};

const NUMBER_FORMATTER_TYPE_FOR_FORMAT: Record<ReturnType<typeof globalValueFormat>, string> = {
  percent: '%',
  currency: '$',
  real: '#',
  decimal: '#'
};

const globalHeaders: CustomModalEditWidgetHeader[] = [
  {
    label: 'Name',
    field: 'name',
    flex: 1,
    minFlexWidth: 300,
    readOnly: true,
    modalRow: 0,
    modalFlex: 4
  },
  {
    label: 'Value',
    field: 'value',
    type: 'decimal',
    modalRow: 0,
    modalFlex: 1,
    renderer: (row: GlobalRow) => (
      <NumberFormatter
        value={row.value}
        type={NUMBER_FORMATTER_TYPE_FOR_FORMAT[globalValueFormat(row.key)]}
        decimals={globalValueFormat(row.key) === 'real' ? 2 : 0}
      />
    ),
    rendererType: (row: GlobalRow) => globalValueFormat(row.key)
  }
];

// Would the user like to update Total Spots to match a new budget? One spot slot per $100 of
// budget, hard-capped at 4000.
const suggestedTotalSpots = (totalBudget: number): number =>
  Math.min(4000, Math.ceil(totalBudget / 100));

interface AudienceDemographicFieldProps {
  data: Partial<AudienceConstraint>;
  onChange: (newData: Partial<AudienceConstraint>) => void;
}

// customRenderer for the Demographic header below -- ModalEditor.tsx calls this directly (it's
// not a component reference, just a function returning ReactNode), so it owns its own picker-open
// state rather than relying on anything from ConstraintView's scope.
const AudienceDemographicField = ({ data, onChange }: AudienceDemographicFieldProps) => {
  const [showPicker, setShowPicker] = useState(false);
  const demoKeys = demographicKeys(data.demographic);

  return (
    <>
      <Button size={Size.LG} appearance={Appearance.OUTLINED} onClick={() => setShowPicker(true)}>
        {demoKeys.length === 0
          ? 'No demographic set'
          : demoKeys.map(key => PRETTY_DEMO_NAMES[key] ?? key).join(', ')}
      </Button>
      {showPicker && (
        <AudiencePickerModal
          selectedDemos={Object.fromEntries(demoKeys.map(key => [key, true]))}
          setSelectedDemos={demos => onChange({ ...data, demographic: Object.keys(demos) })}
          onClose={() => setShowPicker(false)}
        />
      )}
    </>
  );
};

// customRenderer needs a stable function reference (not one redefined inline inside the headers
// useMemo, which react/no-unstable-nested-components correctly flags) and its own param name --
// ModalEditor passes the row's edit-draft as `data`, which would otherwise shadow ConstraintView's
// own `data` state.
const renderAudienceDemographicField = (
  rowData: Partial<AudienceConstraint>,
  onChange: (newData: Partial<AudienceConstraint>) => void
) => <AudienceDemographicField data={rowData} onChange={onChange} />;

export const ConstraintView = ({ company }: LinearOptimizationPageProps) => {
  const { constraintId = '' } = useParams();
  const setError = useSetError();
  const { data: fetchedData, isLoading, error } = useConstraintData(company, constraintId);
  const saveConstraint = useSaveConstraint(company, constraintId);
  const { data: rotations, error: rotationsError } = useNetworkRotations();
  const { data: creativeInsights, error: creativeInsightsError } = useCreativeInsights(company);

  useEffect(() => {
    const queryError = error ?? rotationsError ?? creativeInsightsError;
    if (queryError) {
      setError({
        message: `Failed to load constraint data: ${queryError.message}`,
        reportError: queryError
      });
    }
  }, [error, rotationsError, creativeInsightsError, setError]);
  // mediaTypes:['linear'] needs `streaming: false` explicitly -- the default is `streaming: true`,
  // which would silently group this map by the wrong ('streaming') timeline key. bpm's original
  // LinearOptimizations.tsx never passed this and has carried that bug in production; not
  // replicating it here.
  const { creativeMap } = useCreativeMap({ company, streaming: false, mediaTypes: ['linear'] });

  const {
    data,
    update: updateData,
    discard,
    save,
    changeCount,
    canSave,
    saving,
    validating,
    validationError,
    retryValidation
  } = useConstraintDraft({
    identity: `${company}/${constraintId}`,
    initialData: fetchedData,
    validate: validateLinearConstraint,
    persist: saveConstraint.mutateAsync
  });

  const isTraffic = data?.optimizationType === OptimizationType.TRAFFIC;
  const invalidBudget =
    !isTraffic &&
    (!Number.isFinite(data?.constraints.total) || (data?.constraints.total ?? 0) <= 0);

  const selectorOptions = useMemo(() => {
    const creativeNames = [
      ...new Set(
        Object.values(creativeMap ?? {})
          .filter(creative => Boolean(creative.liveLinear))
          .map(creative => creative.name)
      )
    ].sort();
    const creatives: SelectorOption[] = creativeNames.map(name => ({ label: name, value: name }));

    const networkKeys = Object.keys(rotations?.buyableNetworkAvailRotations ?? {});
    const network: SelectorOption[] = [
      ...networkKeys.map(key => ({ label: key, value: key })),
      { label: 'Each Network', value: 'Each Network' }
    ];
    const networkRotation: Record<string, SelectorOption[]> = Object.fromEntries(
      Object.entries(rotations?.rotationsByNetwork ?? {}).map(([key, rows]) => [
        key,
        rows.map(row => ({ label: row.fullName, value: row.fullName }))
      ])
    );
    const insights: SelectorOption[] = (creativeInsights?.insightsCategoriesData ?? []).map(
      value => ({
        label: value,
        value
      })
    );

    return {
      creatives,
      rotation: ROTATION_OPTIONS,
      avail: AVAIL_OPTIONS,
      length: LENGTH_OPTIONS,
      metric: METRIC_OPTIONS,
      network,
      networkRotation,
      insights
    };
  }, [creativeMap, rotations, creativeInsights]);

  const targetingHeaders = useMemo(
    (): CustomModalEditWidgetHeader[] =>
      isTraffic
        ? []
        : [
            {
              type: 'select',
              field: 'network',
              label: 'Network',
              isMulti: true,
              options: 'network',
              modalRow: 2,
              modalFlex: 1
            },
            {
              type: 'select',
              field: 'reach',
              label: 'Avail',
              options: 'avail',
              modalRow: 2,
              modalFlex: 1
            },
            {
              type: 'select',
              field: 'rotation',
              label: 'Rotation',
              isMulti: true,
              options: 'rotation',
              modalRow: 2,
              modalFlex: 1
            }
          ],
    [isTraffic]
  );

  const budgetHeaders = useMemo(
    (): CustomModalEditWidgetHeader[] => [
      CONSTRAINT_WARNING_HEADER,
      {
        type: 'text',
        field: 'name',
        label: 'Name',
        flex: 1,
        minFlexWidth: 150,
        modalRow: 0,
        modalFlex: 1
      },
      {
        type: 'select',
        field: 'creatives',
        label: 'Creatives',
        isMulti: true,
        options: 'creatives',
        modalRow: 1,
        modalFlex: 1
      },
      {
        type: 'select',
        field: 'insights',
        label: 'Insights',
        isMulti: true,
        options: 'insights',
        modalRow: 1,
        modalFlex: 1
      },
      {
        type: 'select',
        field: 'type',
        label: 'Length',
        options: 'length',
        modalRow: 1,
        modalFlex: 1
      },
      {
        type: 'currency',
        field: 'minValue',
        label: 'Min $',
        width: 100,
        modalRow: 3,
        modalFlex: 1
      },
      { type: 'percent', field: 'minPct', label: 'Min %', width: 100, modalRow: 3, modalFlex: 1 },
      {
        type: 'currency',
        field: 'maxValue',
        label: 'Max $',
        width: 100,
        modalRow: 3,
        modalFlex: 1
      },
      { type: 'percent', field: 'maxPct', label: 'Max %', width: 100, modalRow: 3, modalFlex: 1 },
      ...targetingHeaders
    ],
    [targetingHeaders]
  );

  // Spot's network is a single select (not multi) and its rotation options are network-specific
  // (dynamicOptions keys into selectorOptions.networkRotation by the row's own `network` field),
  // unlike Budget/Audience/Avoid which all share one generic rotation-category list.
  const spotTargetingHeaders = useMemo(
    (): CustomModalEditWidgetHeader[] =>
      isTraffic
        ? []
        : [
            {
              type: 'select',
              field: 'network',
              label: 'Network',
              options: 'network',
              modalRow: 2,
              modalFlex: 1
            },
            {
              type: 'select',
              field: 'reach',
              label: 'Avail',
              options: 'avail',
              modalRow: 2,
              modalFlex: 1
            },
            {
              type: 'select',
              field: 'rotation',
              label: 'Rotation',
              isMulti: true,
              options: 'networkRotation',
              dynamicOptions: (row: Partial<SpotConstraint>) => row.network ?? '',
              flex: 1,
              minFlexWidth: 200,
              modalRow: 2,
              modalFlex: 1
            }
          ],
    [isTraffic]
  );

  const spotHeaders = useMemo(
    (): CustomModalEditWidgetHeader[] => [
      CONSTRAINT_WARNING_HEADER,
      {
        type: 'text',
        field: 'name',
        label: 'Name',
        flex: 1,
        minFlexWidth: 150,
        modalRow: 0,
        modalFlex: 1
      },
      {
        type: 'select',
        field: 'creatives',
        label: 'Creatives',
        isMulti: true,
        options: 'creatives',
        modalRow: 1,
        modalFlex: 1
      },
      {
        type: 'select',
        field: 'insights',
        label: 'Insights',
        isMulti: true,
        options: 'insights',
        modalRow: 1,
        modalFlex: 1
      },
      {
        type: 'select',
        field: 'type',
        label: 'Length',
        options: 'length',
        modalRow: 1,
        modalFlex: 1
      },
      { type: 'decimal', field: 'minValue', label: 'Min', width: 100, modalRow: 3, modalFlex: 1 },
      { type: 'decimal', field: 'maxValue', label: 'Max', width: 100, modalRow: 3, modalFlex: 1 },
      ...spotTargetingHeaders
    ],
    [spotTargetingHeaders]
  );

  const audienceHeaders = useMemo(
    (): CustomModalEditWidgetHeader[] => [
      CONSTRAINT_WARNING_HEADER,
      {
        type: 'text',
        field: 'name',
        label: 'Name',
        flex: 1,
        minFlexWidth: 150,
        modalRow: 0,
        modalFlex: 1
      },
      {
        type: 'select',
        field: 'creatives',
        label: 'Creatives',
        isMulti: true,
        options: 'creatives',
        modalRow: 1,
        modalFlex: 1
      },
      {
        type: 'select',
        field: 'insights',
        label: 'Insights',
        isMulti: true,
        options: 'insights',
        modalRow: 1,
        modalFlex: 1
      },
      {
        type: 'select',
        field: 'type',
        label: 'Length',
        options: 'length',
        modalRow: 1,
        modalFlex: 1
      },
      {
        type: 'select',
        field: 'metric',
        label: 'Metric',
        options: 'metric',
        modalRow: 1,
        modalFlex: 1
      },
      {
        type: 'custom',
        field: 'demographic',
        label: 'Demographic',
        modalRow: 2,
        modalFlex: 2,
        renderer: (row: Partial<AudienceConstraint>) =>
          demographicKeys(row.demographic).length > 0 ? 'Custom' : 'Not set',
        customRenderer: renderAudienceDemographicField
      },
      {
        type: 'decimal',
        field: 'minValue',
        label: 'Min Delivery',
        width: 120,
        modalRow: 3,
        modalFlex: 1
      },
      {
        type: 'checkbox',
        field: 'includeLocals',
        label: 'Include Locals',
        width: 120,
        modalRow: 3,
        modalFlex: 1
      },
      ...targetingHeaders
    ],
    [targetingHeaders]
  );

  const avoidTargetingHeaders = useMemo(
    (): CustomModalEditWidgetHeader[] =>
      isTraffic
        ? []
        : [
            {
              type: 'select',
              field: 'network',
              label: 'Network',
              options: 'network',
              modalRow: 2,
              modalFlex: 1
            },
            {
              type: 'select',
              field: 'reach',
              label: 'Avail',
              options: 'avail',
              modalRow: 2,
              modalFlex: 1
            },
            {
              type: 'select',
              field: 'rotation',
              label: 'Rotation',
              isMulti: true,
              options: 'rotation',
              modalRow: 2,
              modalFlex: 2
            }
          ],
    [isTraffic]
  );

  const avoidHeaders = useMemo(
    (): CustomModalEditWidgetHeader[] => [
      CONSTRAINT_WARNING_HEADER,
      {
        type: 'select',
        field: 'type',
        label: 'Length',
        options: 'length',
        flex: 1,
        modalRow: 0,
        modalFlex: 1
      },
      {
        type: 'select',
        field: 'creatives',
        label: 'Creatives',
        isMulti: true,
        options: 'creatives',
        flex: 1,
        modalRow: 1,
        modalFlex: 1
      },
      {
        type: 'select',
        field: 'insights',
        label: 'Insights',
        isMulti: true,
        options: 'insights',
        modalRow: 1,
        modalFlex: 1
      },
      ...(isTraffic
        ? []
        : ([
            { type: 'checkbox', field: 'rated', label: 'Rated only', modalRow: 2, modalFlex: 1 }
          ] as const)),
      ...avoidTargetingHeaders
    ],
    [avoidTargetingHeaders, isTraffic]
  );

  const globalRows: GlobalRow[] = data
    ? GLOBAL_FIELD_DEFINITIONS.map(([name, key], index) => ({
        index,
        name,
        key,
        value: data.constraints.global[key]
      }))
    : [];

  const budgetInput = useCurrencyInputDisplay(data?.constraints.total ?? 0);

  if (error) {
    return (
      <Page title='Linear Optimizations' pageType='Linear Optimizations'>
        <div className='optimization-inline-error'>
          Failed to load constraint &quot;{constraintId}&quot;.{' '}
          {error instanceof Error ? error.message : String(error)}
        </div>
      </Page>
    );
  }

  if (isLoading || !data) {
    return (
      <Page title='Linear Optimizations' pageType='Linear Optimizations'>
        <FullPageSpinner />
      </Page>
    );
  }

  const applyTable = <T extends { index?: number }>(
    section: 'budget' | 'spot' | 'audience' | 'avoid',
    { addedData, modifiedData, deletedData }: SaveChangesInput<T>
  ) => {
    updateData({
      ...data,
      constraints: {
        ...data.constraints,
        // Re-stamp indexes positionally after the change -- applyTableChanges keys rows by `.index`,
        // but added rows never have one, so leaving them unindexed collides distinct rows sharing
        // `undefined` on the next edit (see withIndexes' comment in utils/optimization-utils.ts).
        [section]: withIndexes(
          applyTableChanges(data.constraints[section] as T[], addedData, modifiedData, deletedData)
        )
      }
    });
  };

  return (
    <Page
      title={
        <span className='linear-optimization-run-title'>
          <Link className='optimization-title-link' to={`/${company}/linear/optimizations`}>
            Linear Optimizations
          </Link>
          <span aria-hidden='true'>/</span>
          <span>{data.name}</span>
        </span>
      }
      pageType='Linear Optimizations'
      minHeight='600px'
      subHeader={
        <ConstraintSaveBar
          changeCount={changeCount}
          disabled={!canSave || invalidBudget}
          saving={saving}
          validating={validating}
          validationError={validationError}
          onRetryValidation={retryValidation}
          onSave={save}
          onDiscard={discard}
        >
          {isTraffic ? (
            <div className='constraint-allocation-week'>Allocation Week: {data.week}</div>
          ) : (
            <Form.Group className='constraint-overall-budget'>
              <Form.Label>Overall Budget</Form.Label>
              <Form.Control
                type='text'
                value={budgetInput.displayValue}
                disabled={saving}
                onFocus={budgetInput.onFocus}
                onChange={event => {
                  budgetInput.onChangeText(event.target.value);
                  const value = parseWholeUsd(event.target.value);
                  updateData({ ...data, constraints: { ...data.constraints, total: value } });
                }}
                onBlur={budgetInput.onBlur}
              />
              {invalidBudget && (
                <span className='text-danger' role='alert'>
                  Overall budget must be greater than zero.
                </span>
              )}
              {changeCount > 0 &&
                data.constraints.global.totalSpots !==
                  suggestedTotalSpots(data.constraints.total) && (
                  <Button
                    appearance={Appearance.OUTLINED}
                    disabled={saving}
                    onClick={() =>
                      updateData({
                        ...data,
                        constraints: {
                          ...data.constraints,
                          global: {
                            ...data.constraints.global,
                            totalSpots: suggestedTotalSpots(data.constraints.total)
                          }
                        }
                      })
                    }
                  >
                    Set total spots to {suggestedTotalSpots(data.constraints.total)}
                  </Button>
                )}
            </Form.Group>
          )}
        </ConstraintSaveBar>
      }
      actions={
        <div className='constraint-page-actions'>
          <span className='last-modified'>
            Last modified{data.lastuser ? ` by ${data.lastuser.split('@')[0]}` : ''}{' '}
            {formatOptimizationDate(data.lastmodified)}
          </span>
        </div>
      }
    >
      <div className='linear-constraint-view'>
        <ModalEditWidget<BudgetConstraint>
          className='budget-constraint-table'
          headers={budgetHeaders}
          name={
            <span className='constraint-widget-title'>
              <MdAttachMoney /> Budget Constraints
            </span>
          }
          initialTableData={data.constraints.budget}
          saveOnEdit
          saveButtonText='Apply'
          readOnly={saving}
          selectorOptions={selectorOptions}
          checkIsValid={checkIsBudgetValid}
          onSaveModal={changes => applyTable('budget', changes)}
        />
        <ModalEditWidget<SpotConstraint>
          className='spot-constraint-table'
          headers={spotHeaders}
          name={
            <span className='constraint-widget-title'>
              <MdBubbleChart /> Spot Constraints
            </span>
          }
          initialTableData={data.constraints.spot}
          saveOnEdit
          saveButtonText='Apply'
          readOnly={saving}
          selectorOptions={selectorOptions}
          checkIsValid={checkIsSpotValid}
          onSaveModal={changes => applyTable('spot', changes)}
        />
        {!isTraffic && (
          <ModalEditWidget<AudienceConstraint>
            className='audience-constraint-table'
            headers={audienceHeaders}
            name={
              <span className='constraint-widget-title'>
                <MdPeople /> Audience Constraints
              </span>
            }
            initialTableData={data.constraints.audience}
            saveOnEdit
            saveButtonText='Apply'
            readOnly={saving}
            selectorOptions={selectorOptions}
            checkIsValid={checkIsAudienceValid}
            onSaveModal={changes => applyTable('audience', changes)}
          />
        )}
        <div className='linear-constraint-widget-grid'>
          <ModalEditWidget<AvoidConstraint>
            className='avoid-constraint-table'
            headers={avoidHeaders}
            name={
              <span className='constraint-widget-title'>
                <MdNotInterested /> Avoid Constraints
              </span>
            }
            initialTableData={data.constraints.avoid}
            saveOnEdit
            saveButtonText='Apply'
            readOnly={saving}
            selectorOptions={selectorOptions}
            checkIsValid={checkIsAvoidValid}
            onSaveModal={changes => applyTable('avoid', changes)}
          />
          {!isTraffic && (
            <ModalEditWidget<GlobalRow>
              className='global-constraint-table'
              headers={globalHeaders}
              name={
                <span className='constraint-widget-title'>
                  <MdAllOut /> Global Constraints
                </span>
              }
              initialTableData={globalRows}
              saveOnEdit
              saveButtonText='Apply'
              readOnly={saving}
              selectorOptions={{}}
              enableAdd={false}
              enableDelete={false}
              checkIsValid={checkIsGlobalValid}
              footerText='This sets the start of the Network/Avail/Rotation relaxation window -- the optimizer widens targeting from this value up to +3 when constraints cannot otherwise be met. We do not recommend setting this too high, as it can relax targeting more than intended.'
              checkShowModalFooter={row => row.key === 'minExtrapolation'}
              onSaveModal={changes => {
                const rows = applyTableChanges(
                  globalRows,
                  changes.addedData,
                  changes.modifiedData,
                  changes.deletedData
                );
                const global = Object.fromEntries(
                  rows.map(row => [row.key, row.value])
                ) as unknown as GlobalConstraints;
                updateData({
                  ...data,
                  constraints: {
                    ...data.constraints,
                    global: { ...data.constraints.global, ...global }
                  }
                });
              }}
            />
          )}
        </div>
      </div>
    </Page>
  );
};
